import React, { useState } from 'react';
import SocialAuthButtons from '../components/SocialAuthButtons';

/*
  SignupPage — signup form extracted from App.js.
  Props: navigateTo, setUser, setIsLoggedIn
*/
const SignupPage = ({ navigateTo, setUser, setIsLoggedIn }) => {
  const [signupData, setSignupData] = useState({ name: '', email: '', company: '', password: '' });
  const [signupStep, setSignupStep] = useState('form');
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!signupData.name || !signupData.email || !signupData.company || !signupData.password) {
      setError('Please fill in all fields');
      return;
    }
    if (signupData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setSignupStep('account-type');
  };

  const handleSignupSubmit = async () => {
    if (!selectedAccountType) return;
    setError('');
    setLoading(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...signupData, accountType: selectedAccountType }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Signup failed');
      setUser(data.user);
      setIsLoggedIn(true);
      setSignupData({ name: '', email: '', company: '', password: '' });
      setSignupStep('form');
      setSelectedAccountType(null);
      navigateTo('welcome');
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'Signup failed');
      setSignupStep('form');
    } finally {
      setLoading(false);
    }
  };

  const btnPrimary = {
    backgroundColor: 'var(--accent)',
    color: '#ffffff', border: 'none', borderRadius: '9px', fontWeight: 700,
    cursor: 'pointer', transition: 'background-color 0.2s ease',
    fontFamily: 'inherit',
  };
  const btnPrimaryHover = (e) => {
    e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
  };
  const btnPrimaryLeave = (e) => {
    e.currentTarget.style.backgroundColor = 'var(--accent)';
  };
  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: '8px',
    border: '1px solid var(--border)', backgroundColor: 'var(--bg)', color: 'var(--text-primary)',
    fontSize: '15px', outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };

  return (
        <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 40px' }}>
          <div style={{ width: '100%', maxWidth: signupStep === 'account-type' ? '560px' : '420px', transition: 'max-width 0.2s' }}>

            {/* Logo */}
            <div style={{ textAlign: 'center', marginBottom: '36px' }}>
              <button onClick={() => { navigateTo('company'); setSignupStep('form'); setSelectedAccountType(null); }}
                style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '28px', display: 'inline-block' }}>
                Axigon<span style={{ color: 'var(--accent)' }}>AI</span>
              </button>
              {signupStep === 'form' ? (
                <>
                  <h2 style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px', letterSpacing: '-0.01em' }}>Create Account</h2>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '15px', margin: 0 }}>Get started with Axigon AI</p>
                </>
              ) : (
                <>
                  <h2 style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px', letterSpacing: '-0.01em' }}>What brings you to Axigon?</h2>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '15px', margin: 0 }}>Choose the experience that fits your needs</p>
                </>
              )}
            </div>

            <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '18px', padding: '36px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
              {error && (
                <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '8px', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444', fontSize: '14px' }}>
                  {error}
                </div>
              )}

              {/* ── Step 1: Form ── */}
              {signupStep === 'form' && (
                <>
                <SocialAuthButtons />
                <form onSubmit={handleSignup} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {[
                    { label: 'Full Name', type: 'text', placeholder: 'John Doe', field: 'name' },
                    { label: 'Company Email', type: 'email', placeholder: 'you@company.com', field: 'email' },
                    { label: 'Company Name', type: 'text', placeholder: 'Your Company', field: 'company' },
                    { label: 'Password', type: 'password', placeholder: 'Min 6 characters', field: 'password' },
                  ].map(({ label, type, placeholder, field }) => (
                    <div key={field}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</label>
                      <input type={type} placeholder={placeholder} value={signupData[field]} onChange={e => setSignupData({ ...signupData, [field]: e.target.value })} disabled={loading}
                        style={inputStyle}
                        onFocus={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                        onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'}
                      />
                    </div>
                  ))}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer', lineHeight: 1.5 }}>
                    <input type="checkbox" required style={{ marginTop: '2px', accentColor: 'var(--accent)', flexShrink: 0 }} />
                    <span>I agree to the Terms of Service and Privacy Policy</span>
                  </label>
                  <button type="submit" disabled={loading}
                    style={{ ...btnPrimary, padding: '13px', fontSize: '15px', width: '100%', marginTop: '4px', opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={e => { if (!loading) btnPrimaryHover(e); }}
                    onMouseLeave={e => { if (!loading) btnPrimaryLeave(e); }}
                  >{loading ? 'Creating Account...' : 'Continue'}</button>
                  <p style={{ textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
                    Already have an account?{' '}
                    <button type="button" onClick={() => navigateTo('login')} style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Log in</button>
                  </p>
                </form>
                </>
              )}

              {/* ── Step 2: Account type ── */}
              {signupStep === 'account-type' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    {[
                      { value: 'personal', emoji: '🙋', title: 'Personal', desc: 'Manage my life,\nfinances & career' },
                      { value: 'business', emoji: '🏢', title: 'Business', desc: 'Run my business,\ncompliance & ops' },
                    ].map(opt => {
                      const isSelected = selectedAccountType === opt.value;
                      return (
                        <button key={opt.value} type="button"
                          onClick={() => setSelectedAccountType(opt.value)}
                          style={{
                            padding: '24px 20px', borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
                            backgroundColor: isSelected ? 'rgba(0,194,124,0.06)' : 'var(--bg)',
                            border: isSelected ? '2px solid var(--accent)' : '2px solid var(--border)',
                            transition: 'all 0.18s ease', outline: 'none',
                            boxShadow: isSelected ? '0 0 0 3px rgba(0,194,124,0.12)' : 'none',
                          }}
                          onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'rgba(0,194,124,0.4)'; }}
                          onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--border)'; }}
                        >
                          <div style={{ fontSize: '32px', marginBottom: '12px' }}>{opt.emoji}</div>
                          <div style={{ fontSize: '16px', fontWeight: 700, color: isSelected ? 'var(--accent)' : 'var(--text-primary)', marginBottom: '8px' }}>{opt.title}</div>
                          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55, whiteSpace: 'pre-line' }}>{opt.desc}</div>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={handleSignupSubmit}
                    disabled={!selectedAccountType || loading}
                    style={{ ...btnPrimary, padding: '13px', fontSize: '15px', width: '100%', opacity: (!selectedAccountType || loading) ? 0.45 : 1, cursor: (!selectedAccountType || loading) ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={e => { if (selectedAccountType && !loading) btnPrimaryHover(e); }}
                    onMouseLeave={e => { if (selectedAccountType && !loading) btnPrimaryLeave(e); }}
                  >{loading ? 'Creating Account...' : 'Continue'}</button>

                  <button type="button"
                    onClick={() => { setSignupStep('form'); setSelectedAccountType(null); }}
                    style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', textAlign: 'center', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
                  >← Back</button>
                </div>
              )}
            </div>
          </div>
        </section>
  );
};

export default SignupPage;
